import { queryOptions } from '@tanstack/react-query';

import apiClient from '@/constants/http';
import type { ApiError } from '@/hooks/mutations/auth';
import {
  isNoResponse,
  readStatusCode,
  shouldRetryApiFailure,
} from '@/utils/apiErrors';
import { Logger } from '@/utils/logging';

import type {
  CustomerStateQueryKey,
  CustomerStateRequest,
  CustomerStateResponse,
} from './types';

const fetchCustomerState = async (
  requestData: CustomerStateRequest,
): Promise<CustomerStateResponse> => {
  try {
    if (!requestData.customerId) {
      throw new Error('Customer ID is required');
    }

    const { data } = await apiClient.get<CustomerStateResponse>(
      `/v1/customer/${requestData.customerId}/state`,
    );
    return data;
  } catch (error: unknown) {
    const log = isNoResponse(error) ? Logger.warn : Logger.error;
    log('Error fetching customer state', { error });
    // Parse error response from API
    const apiError: ApiError = {
      message:
        error instanceof Error
          ? error.message
          : 'Failed to fetch customer state',
      code:
        typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        typeof error.code === 'string'
          ? error.code
          : 'CUSTOMER_STATE_ERROR',
      details:
        typeof error === 'object' &&
        error !== null &&
        'details' in error &&
        error.details !== undefined
          ? error.details
          : undefined,
      statusCode: readStatusCode(error),
    };
    throw apiError;
  }
};

export const customerStateQueryOptions = (customerId: string) => {
  return queryOptions<
    CustomerStateResponse,
    ApiError,
    CustomerStateResponse,
    CustomerStateQueryKey
  >({
    enabled: !!customerId,
    queryKey: ['customer', customerId, 'state'],
    // The response includes a server-rendered "Today"/"Tomorrow" message whose
    // correctness depends on wall-clock day boundaries (SQR-15850), so this
    // can't be cached as long as the rest of the customer profile data.
    staleTime: 5 * 60 * 1000, // 5 minutes
    queryFn: () => fetchCustomerState({ customerId }),
    retry: shouldRetryApiFailure,
  });
};
